import { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  canSubmitCustomFieldValue,
  customFieldValueToParams,
} from '../../CustomFieldValues/actions/customFieldValuesActionCreators';
import { requestBodyWithFiles } from '../../../shared/multipartRequestBody';

// Checkout leaves untouched address fields as {country: "US"}; the shared
// validator treats any non-empty key as a partial address that must be
// completed, which would block saving unrelated edits. Treat those as empty.
export const addressIsBlank = (value) => {
  const filled = Object.keys(value || {}).filter((k) => value[k] && String(value[k]).length > 0);
  return filled.length === 0 || (filled.length === 1 && filled[0] === 'country');
};

const sameValue = (a, b) => a.custom_field.id === b.custom_field.id && (a.ticketId || null) === (b.ticketId || null);

export const groupByTicket = (values) => values.reduce((groups, cfv) => {
  if (!cfv.ticketId) return groups;
  const group = groups.find((g) => g.ticketId === cfv.ticketId);
  if (group) group.values.push(cfv);
  else groups.push({ ticketId: cfv.ticketId, label: cfv.ticketLabel, values: [cfv] });
  return groups;
}, []);

export default function useCustomFields({ csrfToken, updateUrl, customFieldValues }) {
  const [values, setValues] = useState(customFieldValues || []);
  const [open, setOpen] = useState(false);
  const [editValues, setEditValues] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const openModal = () => {
    setEditValues(values.map((cfv) => ({
      ...cfv,
      value: cfv.type === 'CustomFieldValues::Address'
        ? (addressIsBlank(cfv.value) ? {} : cfv.value)
        : (cfv.value == null ? '' : cfv.value),
    })));
    setOpen(true);
  };
  const closeModal = () => setOpen(false);

  const change = (updated) => {
    setEditValues((prev) => prev.map((cfv) => (
      sameValue(cfv, updated) ? { ...cfv, ...updated } : cfv
    )));
  };

  // Validate blank addresses as empty: AddressCustomFieldValue's mount effect
  // re-adds defaultCountry, which would otherwise read as a partial address
  // and block saving unrelated edits.
  const canSave = editValues.every((cfv) => canSubmitCustomFieldValue(
    cfv.type === 'CustomFieldValues::Address' && addressIsBlank(cfv.value) ? { ...cfv, value: {} } : cfv,
    { requireCheckedCheckbox: true },
  ));

  const submit = () => {
    setSubmitting(true);
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    const ticketValues = editValues.filter((cfv) => cfv.ticketId);
    return axios.patch(updateUrl, requestBodyWithFiles('order', {
      custom_field_values_attributes: editValues.filter((cfv) => !cfv.ticketId).map(customFieldValueToParams),
      ...(ticketValues.length > 0 ? {
        ticket_custom_field_values_attributes: ticketValues.map((cfv) => ({ ...customFieldValueToParams(cfv), ticket_id: cfv.ticketId })),
      } : {}),
    }))
      .then(({ data }) => {
        setValues(data.customFieldValues || []);
        setOpen(false);
        toast.success('Custom fields updated', { position: toast.POSITION.TOP_CENTER, hideProgressBar: true });
      })
      .catch((error) => {
        const data = error.response && error.response.data;
        const msg = Array.isArray(data) ? data.join(', ') : 'Could not update custom fields. Please try again.';
        toast.error(msg, { position: toast.POSITION.TOP_CENTER, hideProgressBar: true });
      })
      .finally(() => setSubmitting(false));
  };

  return { values, open, openModal, closeModal, editValues, change, canSave, submitting, submit };
}
